import Hls from 'hls.js';

// WebinarViewer: scales to thousands of concurrent viewers because media is
// server-mixed HLS (cached by Cloudflare's edge) — viewers open NO WebRTC
// connection at all. Chat, polls and presence ride the normal room signaling.

interface ChatMsg {
  sender: string;
  content: string;
  self?: boolean;
  ts: number;
}

export class WebinarViewer {
  private webinarId: string;
  private container: HTMLElement;
  private status: any = null;
  private viewerSid = 'sid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  private statusTimer: any = null;
  private hls: Hls | null = null;
  private signaling: any = null;
  private myToken = '';
  private myName = '';
  private myId = '';
  private chat: ChatMsg[] = [];
  private hlsFailed = false;

  constructor(webinarId: string, container: HTMLElement) {
    this.webinarId = webinarId;
    this.container = container;
  }

  public async init() {
    this.container.innerHTML = `
      <div style="position: fixed; inset: 0; background: #05070d; color: #e5e7eb; display: flex; flex-direction: column; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; z-index: 5000;">
        <header style="display: flex; align-items: center; gap: 14px; padding: 12px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); flex-shrink: 0;">
          <span class="text-gradient" style="font-weight: 800; font-size: 1.1rem;">Verve Webinar</span>
          <span id="wv-live-badge" style="display: none; background: #dc2626; color: #fff; font-weight: 800; font-size: 0.7rem; padding: 3px 10px; border-radius: 9999px; letter-spacing: 0.06em;">● LIVE</span>
          <span id="wv-offline-badge" style="background: rgba(255,255,255,0.08); color: #94a3b8; font-weight: 700; font-size: 0.7rem; padding: 3px 10px; border-radius: 9999px;">STANDBY</span>
          <span id="wv-viewers" style="margin-left: auto; color: #94a3b8; font-size: 0.85rem;"></span>
          <button id="wv-exit" style="background: rgba(255,255,255,0.08); border: none; color: #e5e7eb; padding: 6px 14px; border-radius: 8px; cursor: pointer; font-weight: 600;">Exit</button>
        </header>
        <main style="flex: 1; display: flex; min-height: 0;">
          <section style="flex: 1; display: flex; align-items: center; justify-content: center; position: relative; background: #000;">
            <video id="wv-video" autoplay playsinline controls style="max-width: 100%; max-height: 100%; width: 100%;"></video>
            <div id="wv-waiting" style="position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; background: rgba(0,0,0,0.75); backdrop-filter: blur(4px);">
              <div style="font-size: 3rem;">🎬</div>
              <div style="font-weight: 700; font-size: 1.1rem;">The webinar hasn't started yet</div>
              <div style="color: #94a3b8; font-size: 0.9rem;">This page will switch to the live stream automatically — you can chat below in the meantime.</div>
            </div>
          </section>
          <aside style="width: 360px; border-left: 1px solid rgba(255,255,255,0.08); display: flex; flex-direction: column; flex-shrink: 0;">
            <div style="padding: 10px 16px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; font-size: 0.9rem;">💬 Live Chat</div>
            <div id="wv-chat" style="flex: 1; overflow-y: auto; padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; font-size: 0.88rem;"></div>
            <div id="wv-chat-join" style="padding: 12px 16px; border-top: 1px solid rgba(255,255,255,0.08); display: flex; gap: 8px;">
              <input id="wv-name" placeholder="Your name" style="flex: 1; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #e5e7eb; padding: 8px 10px; border-radius: 8px; outline: none;" />
              <button id="wv-join-chat" style="background: linear-gradient(135deg, #6366f1, #ec4899); border: none; color: white; padding: 8px 14px; border-radius: 8px; font-weight: 700; cursor: pointer;">Join Chat</button>
            </div>
            <div id="wv-chat-send" style="padding: 12px 16px; border-top: 1px solid rgba(255,255,255,0.08); display: none; gap: 8px;">
              <input id="wv-msg" placeholder="Say something…" maxlength="500" style="flex: 1; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #e5e7eb; padding: 8px 10px; border-radius: 8px; outline: none;" />
              <button id="wv-send" style="background: #6366f1; border: none; color: white; padding: 8px 14px; border-radius: 8px; font-weight: 700; cursor: pointer;">Send</button>
            </div>
          </aside>
        </main>
      </div>
    `;

    this.container.querySelector('#wv-exit')?.addEventListener('click', () => {
      window.location.href = '/';
    });
    this.container.querySelector('#wv-join-chat')?.addEventListener('click', () => this.joinChat());
    this.container.querySelector('#wv-send')?.addEventListener('click', () => this.sendChat());
    this.container.querySelector('#wv-msg')?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') this.sendChat();
    });

    await this.refreshStatus();
    this.statusTimer = setInterval(() => this.refreshStatus(), 15000);
  }

  private async refreshStatus() {
    try {
      const res = await fetch(`/api/v1/webinars/${this.webinarId}?viewer_sid=${this.viewerSid}`);
      if (!res.ok) {
        document.getElementById('wv-waiting')!.innerHTML = `<div style="color:#f87171;font-weight:700;">This webinar does not exist.</div>`;
        clearInterval(this.statusTimer);
        return;
      }
      this.status = await res.json();
      const live = !!this.status.live;
      (document.getElementById('wv-live-badge') as HTMLElement).style.display = live ? 'inline-block' : 'none';
      (document.getElementById('wv-offline-badge') as HTMLElement).style.display = live ? 'none' : 'inline-block';
      document.getElementById('wv-viewers')!.textContent = `👥 ${this.status.viewers ?? 0} watching`;
      if (live) {
        (document.getElementById('wv-waiting') as HTMLElement).style.display = 'none';
        this.attachStream(this.status.hls_url);
      } else {
        (document.getElementById('wv-waiting') as HTMLElement).style.display = 'flex';
        this.detachStream();
      }
    } catch (_) {
      /* transient — next tick retries */
    }
  }

  private attachStream(url: string) {
    if (this.hlsFailed) return;
    const video = document.getElementById('wv-video') as HTMLVideoElement;
    if (!video) return;
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      if (video.src !== location.origin + url) video.src = url;
      video.play().catch(() => {});
      return;
    }
    if (Hls.isSupported() && !this.hls) {
      this.hls = new Hls({ lowLatencyMode: false, backBufferLength: 30 });
      this.hls.loadSource(url);
      this.hls.attachMedia(video);
      this.hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          this.hls?.destroy();
          this.hls = null;
          setTimeout(() => this.attachStream(url), 3000);
        }
      });
    }
  }

  private detachStream() {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
      const video = document.getElementById('wv-video') as HTMLVideoElement | null;
      if (video) video.removeAttribute('src');
    }
  }

  private async joinChat() {
    const nameEl = document.getElementById('wv-name') as HTMLInputElement;
    this.myName = (nameEl?.value || '').trim().slice(0, 40);
    if (!this.myName) {
      nameEl?.focus();
      return;
    }
    const roomId = this.status?.room_id;
    if (!roomId) return;
    try {
      const res = await fetch(`/api/v1/meetings/${roomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: `${this.myName} (viewer)` }),
      });
      const data = await res.json();
      this.myToken = data.token;
      this.myId = data.participant_id;
    } catch (_) {
      return;
    }
    if (!this.myToken) return;

    // connect signaling ONLY for chat (no WebRTC as a viewer)
    const mod: any = await import('../services/signaling');
    this.signaling = new mod.SignalingClient();
    this.signaling.connect(this.myToken, roomId);
    this.signaling.on('chat_message', (p: any) => {
      if (p.target_id && p.target_id !== this.myId) return;
      this.pushChat(p.sender_name || 'Viewer', p.content, p.sender_id === this.myId);
    });
    (document.getElementById('wv-chat-join') as HTMLElement).style.display = 'none';
    (document.getElementById('wv-chat-send') as HTMLElement).style.display = 'flex';
    this.systemMsg(`Welcome, ${this.myName}! Be nice — the host can see this chat.`);
  }

  private sendChat() {
    const el = document.getElementById('wv-msg') as HTMLInputElement;
    const content = (el?.value || '').trim();
    if (!content || !this.signaling) return;
    this.signaling.send('chat_message', { content, target_id: '' });
    el.value = '';
  }

  private pushChat(sender: string, content: string, self = false) {
    if (!content) return;
    this.chat.push({ sender, content, self, ts: Date.now() });
    const box = document.getElementById('wv-chat');
    if (!box) return;
    const el = document.createElement('div');
    el.style.cssText = `background: rgba(255,255,255,0.05); padding: 8px 10px; border-radius: 10px; ${self ? 'border: 1px solid rgba(99,102,241,0.4);' : ''}`;
    el.innerHTML = `<div style="font-weight: 700; font-size: 0.75rem; color: ${self ? '#a5b4fc' : '#818cf8'};">${sender}</div><div style="margin-top: 2px; word-break: break-word;">${content.replace(/</g, '&lt;')}</div>`;
    box.appendChild(el);
    box.scrollTop = box.scrollHeight;
  }

  private systemMsg(text: string) {
    const box = document.getElementById('wv-chat');
    if (!box) return;
    const el = document.createElement('div');
    el.style.cssText = 'color: #64748b; font-size: 0.8rem; text-align: center; padding: 4px;';
    el.textContent = text;
    box.appendChild(el);
  }

  public cleanup() {
    clearInterval(this.statusTimer);
    this.detachStream();
    if (this.signaling) {
      this.signaling.close();
      this.signaling = null;
    }
  }
}
